import React, { useContext, useEffect, useRef, useState } from 'react';
import { ModuleBase, ReportGenerator, UserContext } from '@/components';
import {
  AnalyticsBuilderFieldSetType,
  AnalyticsBuilderFieldTypeType,
  AnalyticsBuilderTemplateType,
  CognitiveSearchFilterModel,
  CognitiveSearchRequestModel,
  CognitiveSearchResultModel,
  ModuleBaseModel,
  ModuleSectionPropsModel,
  TechLaunchDatabaseModel,
} from '@/types';
import SearchTermInput from '@/components/search/SearchTermInput';
import SearchFilterOptions from '@/components/search/SearchFilterOptions';
import { ReportsSearchFilterType } from '@/components/search/SearchFilters';
import TechLaunchDatabaseSearchItem from './TechLaunchDatabaseSearchItem';
import Paginator from '@/components/search/Paginator';
import SearchFilterDateRange from '@/components/search/SearchFilterDateRange';
import SearchFilterChips from '@/components/search/SearchFilterChips';
import { DateRange } from 'react-day-picker';
import { useTranslations } from 'next-intl';
import { searchTechDatabase } from '@/api';
import GDLogo from '@/assets/gd-logo.svg';

const fieldSets: AnalyticsBuilderFieldSetType[] = [
  {
    name: 'Search Text Input',
    fields: [
      {
        type: AnalyticsBuilderFieldTypeType.SearchText,
        queryParameterName: 'searchText',
      },
    ],
  },
];


const prompts: AnalyticsBuilderTemplateType[] = [
  {
    "displayName": "Innovation Landscape",
    "description": "<p>Map innovation activity across launches, patents, R&amp;D signals, emerging players, and technology themes.</p>",
    "inputDescription": "<p>Complete each field below</p>",
    "urlSegement": "innovationlandscape",
    "areas": [
      {
        "displayName": "Market Potential Drivers/Inhibitors",
        "urlSegement": "marketpotentialdriversinhibitors"
      },
      {
        "displayName": "Startups",
        "urlSegement": "startups"
      },
      {
        "displayName": "Partnerships",
        "urlSegement": "partnerships"
      },
      {
        "displayName": "Use Cases",
        "urlSegement": "usecases"
      },
      {
        "displayName": "Latest Developments",
        "urlSegement": "latestdevelopments"
      },
      {
        "displayName": "CXO Outlook",
        "urlSegement": "cxooutlook"
      }
    ],
    "fieldSetNames": [
      "Search Text Input"
    ]
  }
];


const formatLocalDateToIso = (date?: Date) => {
  if (!date) {
    return null;
  }

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');

  return `${year}-${month}-${day}`;
};

type TechLaunchDatabaseSearchModuleModel = ModuleBaseModel<
  {
    availableFilters: ReportsSearchFilterType[];
  } & ModuleSectionPropsModel
>;

const TechLaunchDatabaseSearchModule = (data: TechLaunchDatabaseSearchModuleModel) => {
  const { availableFilters: _availableFilters } = data.props;
  const availableFilters = _availableFilters.map((filter) => ({ ...filter, key: filter.key.toLowerCase() }));
  const pageSize = 12;
  const getSkeletons = (numberOfSkeletons: number) => {
    const skeletons: TechLaunchDatabaseModel[] = [];
    for (let index = 0; index < numberOfSkeletons; index++) {
      skeletons.push({ innovationId: index.toString(), loading: true } as TechLaunchDatabaseModel);
    }
    return skeletons;
  };

  const [searchTerm, setSearchTerm] = useState('');
  const [appliedSearchTerm, setAppliedSearchTerm] = useState('');
  const [dateRange, setDateRange] = useState<DateRange | undefined>(undefined);
  const [filterValues, setFilterValues] = useState<{ [key: string]: string[] }>({});
  const [page, setPage] = useState(1);
  const lastRequestIdRef = useRef(0);

  const user = useContext(UserContext);

  const [result, setResult] = useState<CognitiveSearchResultModel<TechLaunchDatabaseModel>>({
    facets: [],
    results: getSkeletons(pageSize),
    totalResults: 0,
  });

  const t = useTranslations();

  const getData = () => {
    const requestId = lastRequestIdRef.current + 1;
    lastRequestIdRef.current = requestId;

    setResult((prev) => ({
      ...prev,
      results: getSkeletons(pageSize),
    }));

    const filters: CognitiveSearchFilterModel[] = filterValues
      ? Object.entries(filterValues)
        .filter(([, values]) => values.length > 0)
        .flatMap(([key, values]) => {
          return {
            name: key,
            relation: 'eq',
            operator: 'and',
            value: values,
          };
        })
      : [];

    if (dateRange?.from) {
      filters.push({
        name: 'publishedDate',
        relation: 'ge',
        operator: 'and',
        value: [formatLocalDateToIso(dateRange.from) + 'T00:00:00+00:00'],
      });
    }
    if (dateRange?.to) {
      filters.push({
        name: 'publishedDate',
        relation: 'le',
        operator: 'and',
        value: [formatLocalDateToIso(dateRange.to) + 'T23:59:59+00:00'],
      });
    }

    const filterRequest: CognitiveSearchRequestModel = {
      facetsMode: 'IncludeFacets',
      searchTerm: appliedSearchTerm || '*',
      filters,
      pageSize: pageSize,
      page: page - 1,
      orderBy: dateRange?.from || dateRange?.to ? 'date' : 'score',
      account: user?.account,
      userId: user?.userId,
    };

    /**
     * If the service is returning 401 unauthorized, it means the token is invalid for the environment.
     * In your .env file, make sure you have the correct AZURE_B2C_CLIENT_ID, AZURE_B2C_CLIENT_SECRET, AZURE_B2C_DOMAIN_NAME, AZURE_B2C_POLICY_NAME and AZURE_B2C_SCOPES for the environment you are working on (UAT or PROD)
     */

    searchTechDatabase(filterRequest).then((dataResponse) => {
      if (requestId !== lastRequestIdRef.current) {
        return;
      }

      setResult({
        facets: [],
        results: dataResponse.results.map((item) => ({
          ...item,
          link: item.url
            ? {
              href: `/tools/tech-launches-database${item.url?.replace('https://disruptor.globaldata.com/innovations', '')}`,
              target: '_blank',
            }
            : undefined,
          innovationImages: item.innovationImages?.imageUrl
            ? item.innovationImages
            : {
              imageUrl: '/Images/tech_launches_database_temp.jpg',
              imageAlt: 'Default Image',
              caption: '',
              copyrightText: '',
              mediaId: 0,
            },
        })),
        totalResults: dataResponse.totalResults,
      });
    });
  };

  useEffect(() => {
    getData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appliedSearchTerm, filterValues, dateRange, page]);

  const handleSubmit = () => {
    setPage(1);
    setAppliedSearchTerm(searchTerm);
  };

  const handleClear = () => {
    setSearchTerm('');
    setPage(1);
    setAppliedSearchTerm('');
    setFilterValues({});
    setDateRange(undefined);
  };

  const handleSetFilterValuesChange = (newValues: { [key: string]: string[] }) => {
    setPage(1);
    setFilterValues(newValues);
  };

  const handleDateRangeChange = (newRange: DateRange | undefined) => {
    setDateRange(newRange);
    setPage(1);
  };

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  return (
    <ModuleBase data={data}>
      <div className="flex w-full justify-center">
        <div className="w-full">
          <div className="container mx-auto px-4">
            <div className="flex w-full justify-center pb-8">
              <div className="flex w-full flex-row items-start md:w-[600px]">
                <GDLogo className="mr-4 h-13 w-13 shrink-0 fill-[#3d87ce]" />
                <div className="flex flex-col">
                  <div className="min-h-10 text-2xl font-semibold text-[#3d87ce] md:h-13 md:text-[34px]">
                    Tech Launches Database
                  </div>
                  <div className="w-full text-base leading-5.5 text-[#1d1d1b]">
                    A curated database of global tech launches organised across
                    <br className="hidden md:block" /> sectors and regions to reveal emerging consumer needs.
                  </div>
                  <ReportGenerator prompts={prompts} fieldSets={fieldSets}>
                    {(openPrompt) => (
                      <div>
                        <button className="btn text-left text-[#3d86cf] underline" onClick={openPrompt}>
                          Generate a report from this dashboard
                        </button>
                      </div>
                    )}
                  </ReportGenerator>
                </div>
              </div>
            </div>

            <div className="rounded-full border border-black/10 px-4 py-2 md:py-4">
              <SearchTermInput
                placeholder="Search All Innovations"
                autofocus={false}
                value={searchTerm}
                onChange={setSearchTerm}
                onSubmit={handleSubmit}
                onClear={handleClear}
                className="[&_.btn]:mr-3 [&_.btn]:capitalize [&_svg]:fill-black/50"
              />
            </div>
          </div>
          <div className="container mx-auto flex flex-col items-center justify-between gap-2 px-4 py-2 md:flex-row md:py-4 lg:py-6">
            <SearchFilterOptions
              availableFilters={availableFilters}
              facets={result.facets}
              isPublic={false}
              value={filterValues}
              onChange={handleSetFilterValuesChange}
            />

            <SearchFilterDateRange value={dateRange} onChange={handleDateRangeChange} className="shrink-0 md:w-64" />
          </div>

          <div className="container mx-auto mb-2 flex flex-col items-start justify-between gap-2 px-4 md:flex-row">
            <div className="w-full">
              <SearchFilterChips
                availableFilters={availableFilters}
                maxDisplayedChips={10}
                singleRow
                value={filterValues}
                onChange={handleSetFilterValuesChange}
              />
            </div>
            <div className="flex shrink-0 items-start md:max-w-fit md:justify-end">
              <SearchFilterChips
                customFilters={[{ label: 'Range', keys: ['from', 'to'], separator: ' → ' }]}
                value={(() => {
                  if (!dateRange?.from || !dateRange?.to) {
                    return null;
                  }

                  const from = formatLocalDateToIso(dateRange.from);
                  const to = formatLocalDateToIso(dateRange.to);

                  if (!from || !to) {
                    return null;
                  }

                  return { from: [from], to: [to] };
                })()}
                onChange={() => handleDateRangeChange(undefined)}
              />
            </div>
          </div>

          <div id="listing-grid" />

          <div className="container">
            <div className="flex w-full flex-col-reverse md:flex-col">
              <div className="flex-1">
                <Paginator
                  pageSize={pageSize}
                  totalResults={result.totalResults || 0}
                  onChange={(data) => handlePageChange(data.page)}
                  value={page}
                  className="!mt-0 md:!mt-3"
                />
              </div>
              <div className="my-2 md:my-0">
                {t('search.$searchtotalresults', {
                  totalResults: result?.totalResults || 0,
                })}
              </div>
            </div>

            <div className="-mx-2 flex flex-wrap">
              {result.results.map((item) => (
                <div key={item.innovationId} className="xxl:w-3/12 w-full p-2 md:w-1/2 lg:w-4/12">
                  <TechLaunchDatabaseSearchItem data={item} />
                </div>
              ))}
            </div>

            <Paginator
              pageSize={pageSize}
              goToElement="#listing-grid"
              totalResults={result.totalResults || 0}
              onChange={(data) => handlePageChange(data.page)}
              value={page}
              className="lg:mt-3"
            />
          </div>
        </div>
      </div>
    </ModuleBase>
  );
};

export default TechLaunchDatabaseSearchModule;
